import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cloud, Zap, Code, Brain, Shield, Users, Layers } from 'lucide-react';
import SEO from '../components/SEO';
import PopupBanner from '../components/PopupBanner';

export default function Home() {
  const showCertifications = false;
  const canonical = 'https://divaisetech.com/';

  const homeSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Divaise Technology Private Limited',
      url: 'https://divaisetech.com',
      logo: 'https://divaisetech.com/divaisetech-logo.png',
      email: 'consult@divaisetech.com',
      telephone: '+91-8287934973',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '13,14 Floor 4, SS Omnia Business Tower, Sector 85',
        addressLocality: 'Gurgaon',
        addressCountry: 'IN',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Divaise Technology',
      url: 'https://divaisetech.com',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://divaisetech.com/?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
  ];

  const services = [
    {
      icon: Cloud,
      title: 'Cloud Solutions',
      description:
        'Architecting scalable, secure cloud platforms on AWS, Azure, and GCP. Migration, optimization, and 24/7 management.',
    },
    {
      icon: Zap,
      title: 'DevOps & Infrastructure',
      description:
        'Automated CI/CD pipelines, Kubernetes orchestration, and infrastructure as code for seamless deployments.',
    },
    {
      icon: Code,
      title: 'Custom Development',
      description:
        'Full-stack web and mobile applications built with modern frameworks and best practices.',
    },
    {
      icon: Brain,
      title: 'AI/ML Services',
      description:
        'Machine learning models, predictive analytics, and AI consulting to unlock data-driven insights.',
    },
    {
      icon: Shield,
      title: 'Security & Compliance',
      description:
        'Vulnerability assessments, penetration testing, and governance-focused compliance support.',
    },
    {
      icon: Layers,
      title: 'Digital Experience',
      description:
        'Adobe Experience Cloud solutions including AEM, Adobe Commerce, AEM Assets, and Adobe Launch.',
    },
    {
      icon: Users,
      title: 'Team Augmentation',
      description:
        'Scale your in-house teams with dedicated engineers and consultants on flexible engagements.',
    },
  ];

  return (
    <div>
      <PopupBanner />
      <SEO
        title="Divaise Technology - IT Consultancy & Software Solutions"
        description="Divaise Technology provides AI-based IT solutions, automation, hardware fulfillment, and AI chip-focused services for modern businesses."
        keywords="AI IT solutions, automation, hardware fulfillment, AI chips, technology consulting"
        canonical={canonical}
        schema={homeSchema}
      />

      {/* Hero Section */}
      <section className="hero-with-image hero-home text-white py-24 px-6 relative overflow-hidden">

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight text-white drop-shadow-lg">
              Building the Future With Confidence
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-100 max-w-3xl mx-auto leading-relaxed text-white">
              A trusted technology partner for organizations that value clarity, reliability,
              and long-term digital progress.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/contact"
              className="inline-flex items-center bg-accent text-black px-8 py-4 rounded-lg font-semibold hover:bg-accent/90 transition-all hover:shadow-lg"
            >
              Get Free Consultation <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/solutions"
              className="inline-flex items-center border-2 border-secondary text-white px-8 py-4 rounded-lg font-semibold hover:bg-secondary/30 hover:text-white transition-all"
            >
              View Our Solutions
            </Link>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-black/40 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <div className="text-3xl font-bold text-accent mb-2">50+</div>
              <div className="text-lg font-semibold mb-2 text-white">Projects Delivered</div>
              <p className="text-white/80">Successfully completed transformative IT initiatives across diverse industries</p>
            </div>
            <div className="bg-black/40 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <div className="text-3xl font-bold text-accent mb-2">150+</div>
              <div className="text-lg font-semibold mb-2 text-white">Tech Professionals</div>
              <p className="text-white/80">Cross-functional engineering and consulting teams aligned to your goals</p>
            </div>
            <div className="bg-black/40 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <div className="text-3xl font-bold text-accent mb-2">$50M+</div>
              <div className="text-lg font-semibold mb-2 text-white">Value Delivered</div>
              <p className="text-white/80">Cost savings and efficiency gains provided to our clients</p>
            </div>
          </div>

          {/* Key Benefits */}
          <div className="bg-black/30 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-6 text-center">Why Partner With Divaise Technology?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                    <span className="text-black font-bold">✓</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Scalable Delivery Framework</h3>
                  <p className="text-white/70 text-sm">Structured planning and execution from discovery to rollout</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                    <span className="text-black font-bold">✓</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Security-First Approach</h3>
                  <p className="text-white/70 text-sm">Built-in controls and governance throughout delivery</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                    <span className="text-black font-bold">✓</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">24/7 Support</h3>
                  <p className="text-white/70 text-sm">Round-the-clock managed services for peace of mind</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                    <span className="text-black font-bold">✓</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Proven Track Record</h3>
                  <p className="text-white/70 text-sm">Consistent delivery with measurable business outcomes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showCertifications && (
        <section className="bg-black py-8 px-6">
          <div className="max-w-6xl mx-auto">
            <p className="text-center text-white/70 mb-6 font-semibold text-sm uppercase tracking-wider">
              Trusted by Industry Leaders
            </p>
          </div>
        </section>
      )}

      {/* Core Solutions */}
      <section className="bg-black py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-white">Our Core Solutions</h2>
          <p className="text-center text-purple-300 text-lg mb-16 max-w-2xl mx-auto">
            Comprehensive IT services designed to accelerate your digital transformation and business growth
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={service.title}
                  className="bg-white border border-border rounded-xl p-8 shadow-sm hover:shadow-lg hover:border-secondary transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-black">{service.title}</h3>
                  <p className="text-black/80 leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hardware Solutions */}
      <section className="bg-gray-950 py-20 px-6 border-y border-gray-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-white">Hardware Solutions</h2>
          <p className="text-center text-white/70 text-lg mb-12 max-w-3xl mx-auto">
            End-to-end enterprise hardware strategy, procurement, and fulfillment for modern infrastructure and AI workloads.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="bg-black border border-gray-700 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-2">Enterprise Procurement</h3>
              <p className="text-white/70">
                Vendor-neutral sourcing for servers, networking, and storage with transparent commercial planning.
              </p>
            </div>
            <div className="bg-black border border-gray-700 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-2">AI and GPU Infrastructure</h3>
              <p className="text-white/70">
                Specialized hardware advisory and procurement for AI training, inference, and high-performance computing.
              </p>
            </div>
            <div className="bg-black border border-gray-700 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-2">Fulfillment and Lifecycle</h3>
              <p className="text-white/70">
                Delivery coordination, deployment readiness, and lifecycle support to keep infrastructure reliable.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/it-hardware-solutions"
              className="inline-flex items-center bg-accent text-black px-7 py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all"
            >
              Explore Hardware Solutions <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/request-hardware-quote"
              className="inline-flex items-center border border-gray-600 text-white px-7 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-all"
            >
              Request Hardware Quote
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-gray-900 py-20 px-6 border-y border-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="bg-black/40 border border-gray-700 rounded-2xl p-8 md:p-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Digital Experience Solutions</h2>
            <p className="text-white/80 mb-8 max-w-3xl">
              We design and implement Adobe Cloud solutions to power content, commerce, and campaign
              experiences across channels.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="bg-white rounded-xl p-5 border border-border">
                <h3 className="text-lg font-bold text-black mb-2">Adobe Experience Manager</h3>
                <p className="text-black/80 text-sm">AEM Sites implementation, component development, and authoring workflows.</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-border">
                <h3 className="text-lg font-bold text-black mb-2">Adobe Commerce</h3>
                <p className="text-black/80 text-sm">Commerce experience design, storefront integration, and performance tuning.</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-border">
                <h3 className="text-lg font-bold text-black mb-2">AEM Assets</h3>
                <p className="text-black/80 text-sm">Digital asset lifecycle setup, taxonomy, governance, and delivery optimization.</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-border">
                <h3 className="text-lg font-bold text-black mb-2">Adobe Launch</h3>
                <p className="text-black/80 text-sm">Tag management strategy, analytics integrations, and release governance.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-gray-900 py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-white">Why Choose Divaise?</h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-secondary text-white font-bold">
                    ✓
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2 text-white">Proven Track Record</h3>
                  <p className="text-white/70">
                    Reliable delivery across cloud, DevOps, security, and AI initiatives.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-secondary text-white font-bold">
                    ✓
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2 text-white">Expert Team</h3>
                  <p className="text-white/70">
                    Engineering and architecture teams with strong hands-on delivery capability.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-secondary text-white font-bold">
                    ✓
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2 text-white">Measurable Results</h3>
                  <p className="text-white/70">
                    Focus on business outcomes with average 40% cost reduction and 60% efficiency gain.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-accent text-white font-bold">
                    ✓
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2 text-white">Flexible Engagement</h3>
                  <p className="text-white/70">
                    Project-based, time & materials, or dedicated team models tailored to your needs.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-accent text-white font-bold">
                    ✓
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2 text-white">24/7 Support</h3>
                  <p className="text-white/70">
                    Round-the-clock managed services and support for production systems.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-accent text-white font-bold">
                    ✓
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2 text-white">Transparent Communication</h3>
                  <p className="text-white/70">
                    Regular updates, detailed reporting, and collaborative approach with all clients.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-white py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl opacity-90 mb-8">
            Schedule a free 30-minute consultation with our experts to discuss your technology challenges.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-accent text-black px-8 py-4 rounded-lg font-semibold hover:bg-accent/90 transition-all hover:shadow-lg"
          >
            Schedule Consultation <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
